import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  listVenuesBasic,
  getGroundSlotsForDate,
  searchUsersClient,
} from "./reservationsApi";
import { listVenueGrounds as listVenueGroundsByVenue } from "../../SuperAdmin/Venues/venueGroundsApi";
import { listPricing as listPricingByGround } from "../../SuperAdmin/Venues/pricingApi";

import FormModal from "../../../components/form/FormModal";
import FormField from "../../../components/form/FormField";
import FormInput from "../../../components/form/FormInput";
import FormSelect from "../../../components/form/FormSelect";

import "./reservation.css";

const toMinutes = (hhmm) => {
  const [h, m] = (hhmm || "00:00").split(":").map((x) => parseInt(x, 10) || 0);
  return h * 60 + m;
};

const to12Label = (startHH, endHH) => {
  const fmt = (HH) => {
    const h = parseInt(HH, 10);
    const mer = h >= 12 ? "PM" : "AM";
    let hh = h % 12;
    if (hh === 0) hh = 12;
    return `${String(hh).padStart(2, "0")}:00 ${mer}`;
  };

  return `${fmt(startHH)} – ${fmt(endHH)}`;
};

const e164Re = /^\+\d{7,15}$/;

const splitE164 = (s) => {
  const m = String(s || "").match(/^\+(\d{1,4})(\d{6,14})$/);
  return m ? { countryCode: `+${m[1]}`, mobileNumber: m[2] } : null;
};

const ccRe = /^\+\d{1,4}$/;

const onlyDigits = (s) => String(s || "").replace(/\D/g, "");

const normalizeMobile10 = (s) => onlyDigits(s).slice(0, 10);

const buildE164 = (cc, mobile) => {
  const c = String(cc || "").trim();
  const m = onlyDigits(mobile);

  if (!ccRe.test(c) || m.length < 6 || m.length > 14) return "";

  return `${c}${m}`;
};

const matchesUserMobile = (user, countryCode, mobile) => {
  const mobileDigits = onlyDigits(mobile);
  const e164 = buildE164(countryCode, mobileDigits);

  if (!mobileDigits || !e164) return false;

  const userMobile = onlyDigits(user?.phone);
  const userCountryCode = String(user?.countryCode || "").trim();
  const userPhoneE164 = String(user?.phoneE164 || "").trim();

  const userBuiltE164 =
    userCountryCode && userMobile ? `${userCountryCode}${userMobile}` : "";

  return (
    userPhoneE164 === e164 ||
    userBuiltE164 === e164 ||
    userMobile === mobileDigits
  );
};

const dialFromISO2 = (iso2) => {
  const c = String(iso2 || "").trim().toUpperCase();

  if (c === "NP") return "+977";
  if (c === "IN") return "+91";
  if (c === "SA") return "+966";
  if (c === "AE") return "+971";

  return "";
};

const padHHMM = (s) => (s || "").padStart(5, "0").slice(0, 5);

const todayYMD = () => new Date().toISOString().slice(0, 10);

const hourCoversByRule = (hourStart, hourEnd, ruleStart, ruleEnd) => {
  const hs = toMinutes(hourStart);
  const he = toMinutes(hourEnd);
  const rs = toMinutes(ruleStart);
  const re = toMinutes(ruleEnd);

  return rs <= hs && re >= he;
};

const dowUTC = (isoDate) => new Date(`${isoDate}T00:00:00Z`).getUTCDay();

const getDateInfo = (isoDate) => {
  if (!isoDate) return "";

  const selected = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(selected.getTime())) return "";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const selectedDay = new Date(selected);
  selectedDay.setHours(0, 0, 0, 0);

  const diffDays = Math.round((selectedDay - today) / (24 * 60 * 60 * 1000));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Tomorrow";
  if (diffDays === -1) return "Yesterday";

  return selected.toLocaleDateString("en-IN", { weekday: "long" });
};

const formatSelectedDateLabel = (isoDate) => {
  if (!isoDate) return "—";

  const date = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(date.getTime())) return isoDate;

  const day = date.toLocaleDateString("en-IN", { day: "2-digit" });
  const month = date.toLocaleDateString("en-IN", { month: "short" });
  const year = date.getFullYear();
  const weekday = date.toLocaleDateString("en-IN", { weekday: "long" });

  return `${day} ${month}, ${year} ${weekday}`;
};

const emptyForm = {
  userId: "",
  userName: "",
  email: "",
  contactNumber: "",
  venue: "",
  venueGround: "",
  date: todayYMD(),
  reservationDates: [],
  totalAmount: "",
  paidAmount: "",
  venueAdminCountryCode: "+977",
  venueAdminMobile: "",
};

const ReservationFormAdmin = ({
  open = true,
  mode = "create",
  initialData,
  onCancel,
  onSubmit,
  isVenueAdmin = false,
}) => {
  const [venues, setVenues] = useState([]);
  const [venueAdminMulti, setVenueAdminMulti] = useState(false);
  const [grounds, setGrounds] = useState([]);

  const [loadingVenues, setLoadingVenues] = useState(true);
  const [loadingGrounds, setLoadingGrounds] = useState(false);

  const [userQ, setUserQ] = useState("");
  const [userDrop, setUserDrop] = useState([]);
  const [showUserDrop, setShowUserDrop] = useState(false);

  const [form, setForm] = useState(() => {
    if (!initialData) return emptyForm;

    return {
      ...emptyForm,
      ...initialData,
      date: initialData?.date || todayYMD(),
      totalAmount:
        typeof initialData?.totalAmount === "number"
          ? String(initialData.totalAmount)
          : initialData?.totalAmount ?? "",
      paidAmount:
        typeof initialData?.paidAmount === "number"
          ? String(initialData.paidAmount)
          : initialData?.paidAmount ?? "",
    };
  });

  const [errors, setErrors] = useState({});
  const [pricingRules, setPricingRules] = useState([]);
  const [totalAuto, setTotalAuto] = useState(0);
  const [totalSource, setTotalSource] = useState("base");
  const [currency, setCurrency] = useState("INR");
  const [slots, setSlots] = useState([]);
  const [loadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const set = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;

      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const setCountryCodeAndSyncContact = (countryCode) => {
    setForm((prev) => ({
      ...prev,
      venueAdminCountryCode: countryCode,
      contactNumber: buildE164(countryCode, prev.venueAdminMobile) || "",
    }));
  };

  const setMobileAndSyncContact = (mobileValue) => {
    const mobile = normalizeMobile10(mobileValue);

    setForm((prev) => ({
      ...prev,
      venueAdminMobile: mobile,
      contactNumber: buildE164(prev.venueAdminCountryCode, mobile) || "",
    }));
  };

  useEffect(() => {
    if (!isVenueAdmin) return;

    setShowUserDrop(false);
    setUserDrop([]);
    setUserQ("");

    setForm((f) => ({
      ...f,
      userId: "",
      contactNumber: "",
      venueAdminCountryCode: f.venueAdminCountryCode || "+977",
      venueAdminMobile: "",
    }));
  }, [isVenueAdmin]);

  useEffect(() => {
    if (!initialData) return;

    setForm((f) => ({
      ...f,
      ...initialData,
      date: initialData?.date || f.date,
      totalAmount:
        typeof initialData?.totalAmount === "number"
          ? String(initialData.totalAmount)
          : initialData?.totalAmount ?? f.totalAmount,
      paidAmount:
        typeof initialData?.paidAmount === "number"
          ? String(initialData.paidAmount)
          : initialData?.paidAmount ?? f.paidAmount,
    }));
  }, [initialData]);

  useEffect(() => {
    (async () => {
      setLoadingVenues(true);

      try {
        const vs = await listVenuesBasic();
        setVenues(Array.isArray(vs) ? vs : []);

        if (isVenueAdmin) {
          const ids = (vs || []).map((v) => String(v._id || v.id)).filter(Boolean);

          if (ids.length === 1) {
            setVenueAdminMulti(false);
            setForm((f) => ({ ...f, venue: ids[0] }));
          } else {
            setVenueAdminMulti(true);
            setForm((f) => ({
              ...f,
              venue: f.venue && ids.includes(String(f.venue)) ? f.venue : "",
            }));
          }
        }
      } catch {
        toast.error("Failed to load venues");
      } finally {
        setLoadingVenues(false);
      }
    })();
  }, [isVenueAdmin]);

  useEffect(() => {
    if (isVenueAdmin) return;

    let alive = true;

    const run = async () => {
      const q = (userQ || "").trim();

      if (q.length < 2) {
        if (alive) setUserDrop([]);
        return;
      }

      try {
        const items = await searchUsersClient(q);
        if (alive) setUserDrop((items || []).slice(0, 12));
      } catch {
        if (alive) setUserDrop([]);
      }
    };

    const t = setTimeout(run, 220);

    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [userQ, isVenueAdmin]);

  useEffect(() => {
    if (!form.venue) {
      setGrounds([]);
      setForm((f) => ({ ...f, venueGround: "" }));
      return;
    }

    (async () => {
      setLoadingGrounds(true);

      try {
        const gs = await listVenueGroundsByVenue(form.venue, {
          includeInactive: false,
          pageSize: 300,
        });

        const nextGrounds = Array.isArray(gs) ? gs : [];
        setGrounds(nextGrounds);

        if (isVenueAdmin && nextGrounds.length > 0) {
          setForm((prev) => {
            if (prev.venueGround) return prev;

            const firstGroundId = nextGrounds[0]?._id || nextGrounds[0]?.id;
            if (!firstGroundId) return prev;

            return {
              ...prev,
              venueGround: String(firstGroundId),
              reservationDates: [],
            };
          });
        }
      } catch {
        toast.error("Failed to load venue grounds");
      } finally {
        setLoadingGrounds(false);
      }
    })();
  }, [form.venue, isVenueAdmin]);

  useEffect(() => {
    if (!isVenueAdmin || !form.venue) return;

    const v = venues.find((x) => String(x._id || x.id) === String(form.venue));
    const iso2 = v?.location?.country || v?.country;
    const dial = dialFromISO2(iso2);

    if (dial) {
      setForm((f) => ({
        ...f,
        venueAdminCountryCode: dial,
        contactNumber: buildE164(dial, f.venueAdminMobile) || "",
      }));
    }
  }, [isVenueAdmin, form.venue, venues]);

  useEffect(() => {
    if (!isVenueAdmin) return;

    const mobileDigits = normalizeMobile10(form.venueAdminMobile);
    const e164 = buildE164(form.venueAdminCountryCode, mobileDigits);

    if (!e164 || mobileDigits.length < 6) {
      setForm((prev) => {
        if (!prev.userName && !prev.email) return prev;

        return {
          ...prev,
          userName: "",
          email: "",
        };
      });
      return;
    }

    let alive = true;

    const run = async () => {
      try {
        const users = await searchUsersClient(mobileDigits);

        if (!alive) return;

        const matchedUser = (users || []).find((user) =>
          matchesUserMobile(user, form.venueAdminCountryCode, mobileDigits),
        );

        if (!matchedUser) {
          setForm((prev) => {
            if (!prev.userName && !prev.email) return prev;

            return {
              ...prev,
              userName: "",
              email: "",
            };
          });
          return;
        }

        setForm((prev) => ({
          ...prev,
          userName: matchedUser.name || "",
          email: matchedUser.email || "",
        }));

        clearError("userName");
        clearError("email");
        clearError("contactNumber");
      } catch {
        // Keep manual-entry flow safe if lookup fails.
      }
    };

    const timer = setTimeout(run, 250);

    return () => {
      alive = false;
      clearTimeout(timer);
    };
  }, [isVenueAdmin, form.venueAdminCountryCode, form.venueAdminMobile]);

  useEffect(() => {
    const g = grounds.find((x) => String(x._id || x.id) === String(form.venueGround));
    setCurrency(g?.currency || "INR");
  }, [grounds, form.venueGround]);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!form.venueGround) {
        if (alive) setPricingRules([]);
        return;
      }

      try {
        const rules = await listPricingByGround(form.venueGround);

        if (alive) {
          setPricingRules(Array.isArray(rules) ? rules.filter((r) => r?.active) : []);
        }
      } catch {
        if (alive) setPricingRules([]);
      }
    };

    run();

    return () => {
      alive = false;
    };
  }, [form.venueGround]);

  useEffect(() => {
    const g = grounds.find((x) => String(x._id || x.id) === String(form.venueGround));

    if (
      !g ||
      !Array.isArray(form.reservationDates) ||
      form.reservationDates.length === 0
    ) {
      setTotalAuto(0);
      setTotalSource("base");
      return;
    }

    const selectedSlotPrices = [];
    let allSelectedSlotsHavePrice = true;

    for (const dayEntry of form.reservationDates) {
      const daySlots = Array.isArray(dayEntry.timeSlots) ? dayEntry.timeSlots : [];

      for (const s of daySlots) {
        const slotPrice = Number(s?.slotPrice);

        if (Number.isFinite(slotPrice)) {
          selectedSlotPrices.push(slotPrice);
        } else {
          allSelectedSlotsHavePrice = false;
        }
      }
    }

    if (selectedSlotPrices.length > 0 && allSelectedSlotsHavePrice) {
      const selectedTotal = selectedSlotPrices.reduce((acc, n) => acc + n, 0);

      setTotalAuto(selectedTotal);
      setTotalSource("rule");
      setForm((f) => ({ ...f, totalAmount: String(selectedTotal) }));
      return;
    }

    let sum = 0;
    let usedRule = false;

    for (const dayEntry of form.reservationDates) {
      const dateISO = dayEntry.date;
      const day = dowUTC(dateISO);
      const daySlots = Array.isArray(dayEntry.timeSlots) ? dayEntry.timeSlots : [];

      for (const s of daySlots) {
        const hourStart = padHHMM(s.start);
        const hourEnd = padHHMM(s.end);

        const match = (pricingRules || []).find((r) => {
          if (!r?.active) return false;
          if (!Array.isArray(r?.days) || r.days.length === 0) return false;
          if (!r.days.includes(day)) return false;
          if (!r.startTime || !r.endTime) return false;

          return hourCoversByRule(hourStart, hourEnd, r.startTime, r.endTime);
        });

        if (match) {
          sum += Number(match.price || 0);
          usedRule = true;
        } else {
          sum += Number(g?.basePrice || 0);
        }
      }
    }

    setTotalAuto(sum);
    setTotalSource(usedRule ? "rule" : "base");
    setForm((f) => ({ ...f, totalAmount: String(sum) }));
  }, [grounds, pricingRules, form.venueGround, form.reservationDates]);

  const refreshReserved = useCallback(async () => {
    if (!form.venue || !form.venueGround || !form.date) return;

    const v = venues.find((x) => String(x._id || x.id) === String(form.venue));
    const slug = v?.slug;

    if (!slug) return;

    try {
      const resp = await getGroundSlotsForDate(slug, form.venueGround, form.date, 60);
      const apiSlots = Array.isArray(resp?.slots) ? resp.slots : [];

      const now = new Date();
      const isToday = new Date(form.date).toDateString() === now.toDateString();
      const nowMin = now.getHours() * 60 + now.getMinutes();

      const mapped = apiSlots.map((s) => {
        const start = s.start;
        const end = s.end;

        const selected =
          Array.isArray(form.reservationDates) &&
          form.reservationDates.some(
            (d) =>
              d.date === form.date &&
              Array.isArray(d.timeSlots) &&
              d.timeSlots.some((ts) => ts.start === start && ts.end === end),
          );

        const past = isToday && toMinutes(end) <= nowMin;
        const status = s.available ? "available" : "booked";
        const label12 = to12Label(start.slice(0, 2), end.slice(0, 2));

        const effectivePrice =
          s?.isAppExclusive === true && Number.isFinite(Number(s?.discountedPrice))
            ? Number(s.discountedPrice)
            : Number(s?.price ?? 0);

        return {
          start,
          end,
          label12,
          status,
          past,
          selected,
          price: effectivePrice,
          originalPrice: Number(s?.price ?? 0),
          discountedPrice: Number.isFinite(Number(s?.discountedPrice))
            ? Number(s.discountedPrice)
            : null,
          isAppExclusiveApplied: s?.isAppExclusive === true,
          currency: s.currency,
        };
      });

      setSlots(mapped);
    } catch {
      setSlots([]);
    }
  }, [form.venue, form.venueGround, form.date, venues, form.reservationDates]);

  useEffect(() => {
    refreshReserved();
  }, [refreshReserved]);

  const validate = () => {
    const e = {};
    const manualContact = buildE164(form.venueAdminCountryCode, form.venueAdminMobile);

    if (!form.venue) e.venue = "Venue is required";
    if (!form.venueGround) e.venueGround = "Ground is required";
    if (!form.date) e.date = "Date is required";

    if (!form.reservationDates || form.reservationDates.length === 0) {
      e.reservationDates = "Select at least one slot";
    }

    if (!form.totalAmount && form.totalAmount !== 0) {
      e.totalAmount = "Total is required";
    }

    if (isVenueAdmin) {
      if (!String(form.userName || "").trim()) e.userName = "Name is required";

      if (!manualContact) e.contactNumber = "Mobile number is required";
    } else if (!form.userId && !manualContact && !form.contactNumber) {
      e.contactNumber = "Enter a phone or select a user";
    }

    setErrors(e);

    return Object.keys(e).length === 0;
  };

  const submit = async (event) => {
    event.preventDefault();

    if (submitting) return;

    if (!validate()) {
      toast.error("Please fix validation errors");
      return;
    }

    let contact = (form.contactNumber || "").trim().replace(/\s+/g, "");
    let parts = splitE164(contact);

    const splitContact = buildE164(form.venueAdminCountryCode, form.venueAdminMobile);

    if (isVenueAdmin) {
      contact = splitContact;

      if (!contact || !e164Re.test(contact)) {
        toast.error("Enter valid country code and mobile number");
        return;
      }

      parts = {
        countryCode: form.venueAdminCountryCode,
        mobileNumber: onlyDigits(form.venueAdminMobile),
      };
    } else {
      if (!form.userId) {
        contact = splitContact || contact;
        parts = splitE164(contact);
      }

      if (!form.userId && contact && !e164Re.test(contact)) {
        toast.error("Enter valid country code and mobile number");
        return;
      }
    }

    const clean = (obj) =>
      Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

    const payloadCountryCode =
      form.venueAdminCountryCode || parts?.countryCode || undefined;

    const payloadMobileNumber =
      onlyDigits(form.venueAdminMobile) || parts?.mobileNumber || undefined;

    // Venue-admin creation expects digits-only full E.164 and adds "+" on the
    // backend. Other admin roles pass countryCode separately to
    // ensureUserByContact, so their contactNumber must stay as national digits.
    const payloadContactNumber =
      payloadCountryCode && payloadMobileNumber
        ? isVenueAdmin
          ? `${onlyDigits(payloadCountryCode)}${payloadMobileNumber}`
          : payloadMobileNumber
        : contact
          ? onlyDigits(contact)
          : undefined;

    const basePayload = clean({
      contactNumber: payloadContactNumber,
      userName: String(form.userName || "").trim() || undefined,
      countryCode: payloadCountryCode,
      mobileNumber: payloadMobileNumber,
      venue: form.venue,
      venueGround: form.venueGround,
      reservationDates: form.reservationDates,
      totalAmount: Number(form.totalAmount || 0),
      paidAmount: Number(form.paidAmount || 0),
    });

    const payload = isVenueAdmin
      ? clean({
        ...basePayload,
        email: String(form.email || "").trim().toLowerCase() || undefined,
      })
      : clean({
        ...basePayload,
        userId: form.userId ? form.userId : undefined,
      });

    try {
      setSubmitting(true);
      await onSubmit?.(payload);
    } finally {
      setSubmitting(false);
    }
  };

  const pickHour = (slot) => {
    if (slot.status !== "available" || slot.past) return;

    setForm((f) => {
      const date = f.date;
      const existingDay = f.reservationDates.find((d) => d.date === date);

      let updatedDays;

      if (existingDay) {
        const already = existingDay.timeSlots.some(
          (ts) => ts.start === slot.start && ts.end === slot.end,
        );

        const newSlots = already
          ? existingDay.timeSlots.filter(
            (ts) => !(ts.start === slot.start && ts.end === slot.end),
          )
          : [
            ...existingDay.timeSlots,
            {
              start: slot.start,
              end: slot.end,
              slotPrice: Number(slot.price ?? null),
              isAppExclusiveApplied: slot.isAppExclusiveApplied === true,
              discountedPrice:
                slot.isAppExclusiveApplied === true &&
                  Number.isFinite(Number(slot.discountedPrice))
                  ? Number(slot.discountedPrice)
                  : null,
            },
          ];

        updatedDays = f.reservationDates
          .map((d) => (d.date === date ? { ...d, timeSlots: newSlots } : d))
          .filter((d) => d.timeSlots.length > 0);
      } else {
        updatedDays = [
          ...f.reservationDates,
          {
            date,
            timeSlots: [
              {
                start: slot.start,
                end: slot.end,
                slotPrice: Number(slot.price ?? null),
                isAppExclusiveApplied: slot.isAppExclusiveApplied === true,
                discountedPrice:
                  slot.isAppExclusiveApplied === true &&
                    Number.isFinite(Number(slot.discountedPrice))
                    ? Number(slot.discountedPrice)
                    : null,
              },
            ],
          },
        ];
      }

      return { ...f, reservationDates: updatedDays };
    });

    clearError("reservationDates");
  };

  const venueAdminContactOk = useMemo(() => {
    if (!isVenueAdmin) return true;

    const e164 = buildE164(form.venueAdminCountryCode, form.venueAdminMobile);

    return !!e164 && e164Re.test(e164);
  }, [isVenueAdmin, form.venueAdminCountryCode, form.venueAdminMobile]);

  const superAdminContactOk = useMemo(() => {
    if (isVenueAdmin) return true;
    if (form.userId) return true;

    const e164 =
      buildE164(form.venueAdminCountryCode, form.venueAdminMobile) ||
      String(form.contactNumber || "").trim();

    return !!e164 && e164Re.test(e164);
  }, [
    isVenueAdmin,
    form.userId,
    form.venueAdminCountryCode,
    form.venueAdminMobile,
    form.contactNumber,
  ]);

  const numericTotal = Number(
    String(form.totalAmount ?? "").trim() !== "" ? form.totalAmount : totalAuto,
  );

  const canSubmit =
    (isVenueAdmin
      ? String(form.userName || "").trim().length > 0 && venueAdminContactOk
      : String(form.userId || "").trim().length > 0 || superAdminContactOk) &&
    String(form.venue || "").trim().length > 0 &&
    String(form.venueGround || "").trim().length > 0 &&
    Array.isArray(form.reservationDates) &&
    form.reservationDates.length > 0 &&
    Number.isFinite(numericTotal) &&
    numericTotal >= 0;

  const dateInfo = useMemo(() => getDateInfo(form.date), [form.date]);

  const handleClose = () => {
    if (submitting) return;
    onCancel?.();
  };

  return (
    <FormModal
      open={open}
      title={mode === "create" ? "Create Reservation" : "Edit Reservation"}
      width="860px"
      submitting={submitting}
      submitText={mode === "create" ? "Create Reservation" : "Save Changes"}
      submittingText={mode === "create" ? "Creating..." : "Saving..."}
      cancelText="Cancel"
      onClose={handleClose}
      onSubmit={submit}
    >
      <div className="reservation-form">
        {isVenueAdmin ? (
          <div className="reservation-grid reservation-grid--venue-admin-player">
            <FormField
              label="Mobile Number"
              error={errors.contactNumber}
              className="reservation-mobile-field"
            >
              <div className="reservation-phone-inline">
                <FormSelect
                  value={form.venueAdminCountryCode}
                  className="reservation-country-code-select"
                  onChange={(event) => {
                    setCountryCodeAndSyncContact(event.target.value);
                    clearError("contactNumber");
                  }}
                >
                  <option value="+91">+91</option>
                  <option value="+977">+977</option>
                  <option value="+966">+966</option>
                  <option value="+971">+971</option>
                </FormSelect>

                <FormInput
                  value={form.venueAdminMobile}
                  onChange={(event) => {
                    setMobileAndSyncContact(event.target.value);
                    clearError("contactNumber");
                  }}
                  placeholder="9876543210"
                  inputMode="numeric"
                  maxLength={10}
                  className="reservation-mobile-input"
                />
              </div>
            </FormField>

            <FormField
              label="Player Name"
              error={errors.userName}
              className="reservation-player-name-field"
            >
              <FormInput
                value={form.userName}
                onChange={(event) => {
                  set("userName", event.target.value);
                  clearError("userName");
                }}
                placeholder="Auto-filled if mobile exists"
              />
            </FormField>

            <FormField
              label="Email (optional)"
              error={errors.email}
              className="reservation-email-field"
            >
              <FormInput
                value={form.email}
                onChange={(event) => {
                  set("email", event.target.value);
                  clearError("email");
                }}
                placeholder="Auto-filled if available"
              />
            </FormField>

            <div className="reservation-mobile-first-note reservation-span-3">
              Enter mobile number first. If the player already exists, name and email
              will be filled automatically.
            </div>
          </div>
        ) : (
          <div className="reservation-grid">
            <FormField
              label="Player (search or type)"
              hint="Pick an existing user or type a phone/email to auto-create a pending user."
              className="reservation-user-field"
            >
              <div className="reservation-player-combobox">
                <FormInput
                  placeholder="Search by name / phone / email"
                  value={form.userId ? form.userName || userQ : userQ}
                  onChange={(event) => {
                    setForm((f) => ({ ...f, userId: "", userName: "" }));
                    setUserQ(event.target.value);
                    setShowUserDrop(true);
                  }}
                  onBlur={() => setTimeout(() => setShowUserDrop(false), 120)}
                  onFocus={() => {
                    if ((userQ || "").trim().length > 0 && !form.userId) {
                      setShowUserDrop(true);
                    }
                  }}
                />

                {showUserDrop &&
                  !form.userId &&
                  (userQ.trim().length > 0 || userDrop.length > 0) && (
                    <div
                      className="reservation-user-dropdown"
                      onMouseDown={(event) => event.preventDefault()}
                    >
                      {userDrop.length === 0 && (
                        <div className="reservation-user-option is-hint">
                          {(userQ || "").trim().length < 2
                            ? "Type at least 2 characters…"
                            : "Searching / no matches yet…"}
                        </div>
                      )}

                      {userDrop.map((u) => (
                        <button
                          key={u.id}
                          type="button"
                          className="reservation-user-option"
                          onMouseDown={() => {
                            const nextCountryCode =
                              u.countryCode || form.venueAdminCountryCode || "+977";
                            const nextMobile = normalizeMobile10(u.phone);

                            setForm((f) => ({
                              ...f,
                              userId: u.id,
                              userName: u.name || "",
                              contactNumber:
                                buildE164(nextCountryCode, nextMobile) ||
                                f.contactNumber,
                              venueAdminCountryCode: nextCountryCode,
                              venueAdminMobile: nextMobile,
                            }));

                            setUserQ(`${u.name || "—"} ${u.phone || ""} ${u.email || ""}`);
                            setShowUserDrop(false);
                            clearError("contactNumber");
                          }}
                        >
                          <span>{u.name}</span>
                          <small>{[u.phone, u.email].filter(Boolean).join(" • ")}</small>
                        </button>
                      ))}

                      <div className="reservation-user-separator" />

                      <button
                        type="button"
                        className="reservation-user-option is-create"
                        onMouseDown={() => {
                          const raw = (userQ || "").trim().replace(/\s+/g, "");
                          const split = splitE164(raw);
                          const nextCountryCode =
                            split?.countryCode || form.venueAdminCountryCode || "+977";
                          const nextMobile = normalizeMobile10(
                            split?.mobileNumber || raw,
                          );
                          const nextContact = buildE164(nextCountryCode, nextMobile);

                          setForm((f) => ({
                            ...f,
                            userId: "",
                            contactNumber: nextContact || "",
                            venueAdminCountryCode: nextCountryCode,
                            venueAdminMobile: nextMobile,
                          }));

                          setShowUserDrop(false);
                          clearError("contactNumber");
                        }}
                      >
                        Use typed contact: <strong>{userQ || "—"}</strong>
                      </button>
                    </div>
                  )}
              </div>
            </FormField>

            <FormField label="Contact Number" error={errors.contactNumber}>
              <div className="reservation-phone-inline reservation-phone-inline--super-admin">
                <FormSelect
                  value={form.venueAdminCountryCode}
                  className="reservation-country-code-select"
                  onChange={(event) => {
                    setCountryCodeAndSyncContact(event.target.value);
                    clearError("contactNumber");
                  }}
                >
                  <option value="+91">+91</option>
                  <option value="+977">+977</option>
                  <option value="+966">+966</option>
                  <option value="+971">+971</option>
                </FormSelect>

                <FormInput
                  value={form.venueAdminMobile}
                  onChange={(event) => {
                    setMobileAndSyncContact(event.target.value);
                    clearError("contactNumber");
                  }}
                  onFocus={() => setShowUserDrop(false)}
                  placeholder="9876543210"
                  inputMode="numeric"
                  maxLength={10}
                  className="reservation-mobile-input"
                />
              </div>
            </FormField>
          </div>
        )}

        <div className="reservation-grid reservation-grid--three">
          <FormField label="Venue" error={errors.venue}>
            <FormSelect
              value={form.venue}
              onChange={(event) => {
                setForm((prev) => ({
                  ...prev,
                  venue: event.target.value,
                  venueGround: "",
                  reservationDates: [],
                }));
                clearError("venue");
              }}
              required
              disabled={isVenueAdmin && !venueAdminMulti}
            >
              <option value="">
                {loadingVenues ? "Loading venues…" : "Select a venue…"}
              </option>

              {venues.map((v) => (
                <option key={v._id || v.id} value={v._id || v.id}>
                  {v.name}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField label="Ground" error={errors.venueGround}>
            <FormSelect
              value={form.venueGround}
              onChange={(event) => {
                setForm((prev) => ({
                  ...prev,
                  venueGround: event.target.value,
                  reservationDates: [],
                }));
                clearError("venueGround");
              }}
              disabled={!form.venue || loadingGrounds || grounds.length === 0}
              required
            >
              <option value="">
                {loadingGrounds
                  ? "Loading grounds…"
                  : grounds.length
                    ? "Select a ground…"
                    : "No active grounds"}
              </option>

              {grounds.map((g) => (
                <option key={g._id || g.id} value={g._id || g.id}>
                  {g.name}
                </option>
              ))}
            </FormSelect>
          </FormField>

          <FormField label="Date" error={errors.date}>
            <FormInput
              type="date"
              value={form.date}
              min={todayYMD()}
              onChange={(event) => {
                const nextDate = event.target.value;

                if (nextDate && nextDate < todayYMD()) {
                  toast.error("Past dates cannot be selected");
                  return;
                }

                setForm((prev) => ({
                  ...prev,
                  date: nextDate,
                }));
                clearError("date");
              }}
              required
            />

            {dateInfo ? <small className="reservation-note">{dateInfo}</small> : null}
          </FormField>
        </div>

        <div className="reservation-grid">
          <FormField
            label="Hourly Availability"
            error={errors.reservationDates}
            className="reservation-span-2"
          >
            <div className="reservation-slot-legend">
              <span className="reservation-slot-legend-item">
                <span className="reservation-slot-dot reservation-slot-dot--available" />
                <span>Available</span>
              </span>

              <span className="reservation-slot-legend-item">
                <span className="reservation-slot-dot reservation-slot-dot--booked" />
                <span>Booked</span>
              </span>

              <span className="reservation-slot-legend-item">
                <span className="reservation-slot-dot reservation-slot-dot--blocked" />
                <span>Blocked</span>
              </span>

              <span className="reservation-slot-legend-item">
                <span className="reservation-slot-dot reservation-slot-dot--selected" />
                <span>Selected</span>
              </span>
            </div>
            {!form.venueGround || !form.date ? (
              <div className="reservation-note">Select a ground and date to view slots.</div>
            ) : loadingSlots ? (
              <div className="reservation-note">Loading slots…</div>
            ) : slots.length === 0 ? (
              <div className="reservation-note">No slots generated.</div>
            ) : (
              <div className="reservation-slots-grid">
                {slots.map((s) => {
                  let tone = "";

                  if (s.selected) tone = "success";
                  else if (s.past) tone = "muted";
                  else if (s.status === "booked") tone = "danger";
                  else if (s.status === "blocked") tone = "warn";
                  else tone = "default";

                  const className = [
                    "sa-chip",
                    "sa-chip--slot",
                    `sa-chip--${tone}`,
                    s.selected ? "sa-chip--selected" : "",
                  ].join(" ");

                  return (
                    <button
                      key={s.start}
                      type="button"
                      className={className}
                      onClick={() => pickHour(s)}
                      disabled={s.status !== "available" || s.past}
                      title={
                        s.selected
                          ? "Selected"
                          : s.past
                            ? "Past time"
                            : s.status === "booked"
                              ? "Booked"
                              : s.status === "blocked"
                                ? "Blocked"
                                : "Click to select"
                      }
                    >
                      {s.label12}
                    </button>
                  );
                })}
              </div>
            )}
          </FormField>
        </div>

        {Array.isArray(form.reservationDates) && form.reservationDates.length > 0 && (
          <div className="reservation-grid">
            <FormField label="Selected Slots" className="reservation-span-2">
              <div className="reservation-selected-list">
                {form.reservationDates.map((d) => (
                  <div key={d.date} className="reservation-selected-card">
                    <div className="reservation-selected-title">
                      Date: {formatSelectedDateLabel(d.date)}
                    </div>

                    <div className="reservation-chipset">
                      {(d.timeSlots || []).map((ts) => (
                        <span
                          key={`${d.date}-${ts.start}`}
                          className="sa-chip sa-chip--success sa-chip--slot"
                        >
                          {to12Label(ts.start.slice(0, 2), ts.end.slice(0, 2))}
                          {ts.slotPrice != null ? ` • ${ts.slotPrice} ${currency}` : ""}

                          {ts.isAppExclusiveApplied ? (
                            <span className="reservation-app-only">App Only</span>
                          ) : null}

                          <button
                            type="button"
                            className="reservation-chip-remove"
                            onClick={() => {
                              setForm((f) => {
                                const copy = f.reservationDates
                                  .map((x) =>
                                    x.date === d.date
                                      ? {
                                        ...x,
                                        timeSlots: x.timeSlots.filter(
                                          (z) =>
                                            !(
                                              z.start === ts.start &&
                                              z.end === ts.end
                                            ),
                                        ),
                                      }
                                      : x,
                                  )
                                  .filter((x) => x.timeSlots.length > 0);

                                return { ...f, reservationDates: copy };
                              });
                            }}
                            title="Remove"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </FormField>
          </div>
        )}

        <div className="reservation-grid">
          <FormField label={`Total (${currency})`} error={errors.totalAmount}>
            <FormInput
              type="number"
              min="0"
              value={totalAuto}
              readOnly
              title={
                totalSource === "rule"
                  ? "From matching pricing rule"
                  : "From ground base price"
              }
            />

            <small className="reservation-note">
              {totalSource === "rule" ? (
                <>
                  Applied <strong>pricing rule</strong> for this day/time.
                </>
              ) : (
                <>
                  No rule matched—using <strong>base price</strong> for this ground.
                </>
              )}
            </small>
          </FormField>

          <FormField label={`Advance Payment (${currency})`}>
            <FormInput
              type="number"
              min="0"
              value={form.paidAmount}
              onChange={(event) => set("paidAmount", event.target.value)}
              placeholder="0"
            />
          </FormField>
        </div>

        {!canSubmit ? (
          <div className="reservation-submit-note">
            Fill required details and select at least one available slot to create the
            reservation.
          </div>
        ) : null}
      </div>
    </FormModal>
  );
};

export default ReservationFormAdmin;